import React, { useState } from 'react';
import { Box, Typography, makeStyles, Theme, Tooltip, withStyles } from '@material-ui/core';
import clsx from 'clsx';

interface TimeBlock {
  start: number;
  width: number;
  duration: number;
  enteredAt: string;
  exitedAt?: string;
  isCurrentlyInLocation: boolean;
}

interface RoomTimeBlock {
  room: string;
  type: string;
  blocks: TimeBlock[];
}

interface RoomStat {
  roomName: string;
  type: string;
  percentage: number;
  duration: number;
  cost: number;
}

interface TimelineGraphProps {
  timeBlocks: RoomTimeBlock[];
  stats: RoomStat[];
  timeRange: { start: number; end: number };
}

const StyledTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    fontSize: '14px',
    fontWeight: 400,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    maxWidth: '200px',
    textAlign: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  },
  arrow: {
    color: 'rgba(255, 255, 255, 0.08)',
  },
}))(Tooltip);

const useStyles = makeStyles((theme: Theme) => ({
  timelineContainer: {
    marginTop: theme.spacing(4),
    width: '100%',
    background: 'transparent',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
  },
  timelineWrapper: {
    width: '100%',
    overflowX: 'auto',
  },
  timelineContent: {
    minWidth: '900px',
    paddingBottom: theme.spacing(2),
  },
  timelineGrid: {
    display: 'grid',
    gridTemplateColumns: '100px 1fr',
    position: 'relative',
    marginBottom: theme.spacing(2),
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 100,
      right: 0,
      bottom: 0,
      backgroundImage: `
        linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      `,
      backgroundSize: 'calc((100% - 1px) / var(--hour-count, 12)) 40px',
      pointerEvents: 'none',
    },
  },
  gridRows: {
    display: 'grid',
    gridAutoRows: '40px',
    width: '100%',
    gridColumn: '1 / -1',
  },
  gridRow: {
    display: 'grid',
    gridTemplateColumns: '100px 1fr',
    alignItems: 'center',
    height: '40px',
  },
  gridRowOdd: {
    display: 'grid',
    gridTemplateColumns: '100px 1fr',
    alignItems: 'center',
    height: '40px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  roomLabel: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '13px',
    fontWeight: 400,
    textAlign: 'right',
    paddingRight: theme.spacing(2),
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  timelineRow: {
    position: 'relative',
    height: '40px',
    backgroundColor: 'transparent',
  },
  timeBlock: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    height: '28px',
    borderRadius: 4,
    boxShadow: '0 0 8px rgba(0, 0, 0, 0.2)',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
    '&:hover': {
      opacity: 0.8,
    },
  },
  timeAxis: {
    position: 'relative',
    marginLeft: 100,
    paddingTop: theme.spacing(1),
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  timeLabels: {
    display: 'grid',
    gridTemplateColumns: 'repeat(var(--hour-count, 12), 1fr)',
  },
  timeLabel: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '12px',
    textAlign: 'center',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -10,
      left: 0,
      width: 1,
      height: 4,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  statsContainer: {
    marginTop: theme.spacing(4),
    width: '100%',
  },
  progressBar: {
    height: 4,
    display: 'flex',
    marginBottom: theme.spacing(2),
    gap: '6px',
    '& > div': {
      height: '100%',
      transition: 'width 0.3s ease',
      borderRadius: '2px',
    },
  },
  statsBar: {
    display: 'flex',
    width: '100%',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    '& .header': {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(0.5),
      '& .dot': {
        width: 8,
        height: 8,
        borderRadius: '50%',
        marginRight: theme.spacing(1),
      },
      '& .label': {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '14px',
        marginRight: theme.spacing(1),
      },
    },
    '& .details': {
      display: 'flex',
      flexDirection: 'column',
      '& .percentage': {
        color: '#fff',
        fontSize: '24px',
        fontWeight: 500,
        marginBottom: theme.spacing(0.5),
      },
      '& .time': {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '14px',
        marginBottom: theme.spacing(0.5),
      },
      '& .cost': {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '14px',
      },
    },
  },
}));

const getRoomColor = (type: string): string => {
  switch (type?.toUpperCase()) {
    case 'OR':
    case 'OPERATING_ROOM':
      return '#74AC48';

    case 'PRE_OP':
    case 'PRE-OP':
    case 'PREOP':
      return '#FFBB28';

    case 'POST_OP':
    case 'POST-OP':
    case 'POSTOP':
      return '#3D5A80';

    case 'PACU':
      return '#2B3C87';

    case 'BREAK_ROOM':
    case 'BREAK':
      return '#3D5A80';

    case 'WAITING_ROOM':
    case 'WAITING':
      return '#00A7F7';

    case 'UNKNOWN':
    case 'OFF_SITE':
      return '#9E9E9E';

    default:
      return '#F38181';
  }
};

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  return `${remainingMinutes}m`;
};

const formatTimeLabel = (hour: number, timeRange: { start: number; end: number }): string => {
  const actualHour = timeRange.start + hour;
  if (actualHour === 0 || actualHour === 24) return '12 AM';
  if (actualHour === 12) return '12 PM';
  return `${actualHour > 12 ? actualHour - 12 : actualHour} ${actualHour >= 12 ? 'PM' : 'AM'}`;
};

const TimelineGraph: React.FC<TimelineGraphProps> = ({ timeBlocks, stats, timeRange }) => {
  const classes = useStyles();
  const [hoveredBlock, setHoveredBlock] = useState<{ room: string; duration: number } | null>(null);

  const sortedStats = [...stats].filter(stat => stat.percentage > 0).sort((a, b) => b.percentage - a.percentage);

  const hourCount = timeRange.end - timeRange.start;

  return (
    <Box className={classes.timelineContainer}>
      <Box className={classes.timelineWrapper}>
        <Box className={classes.timelineContent} style={{ '--hour-count': hourCount } as React.CSSProperties}>
          <Box className={classes.timelineGrid}>
            <div className={classes.gridRows}>
              {timeBlocks.map(({ room, blocks, type }, index) => (
                <div key={room} className={clsx(classes.gridRow, index % 2 === 1 ? classes.gridRowOdd : null)}>
                  <Typography className={classes.roomLabel}>{room}</Typography>
                  <div className={classes.timelineRow}>
                    {blocks.map((block, blockIndex) => (
                      <StyledTooltip
                        key={blockIndex}
                        title={
                          <div>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{room}</div>
                            <div style={{ marginBottom: '2px' }}>Duration: {formatDuration(block.duration)}</div>
                            <div style={{ marginBottom: '2px' }}>Entered: {block.enteredAt}</div>
                            {!block.isCurrentlyInLocation && block.exitedAt && <div>Exited: {block.exitedAt}</div>}
                          </div>
                        }
                        placement="top"
                        arrow
                      >
                        <div
                          className={clsx(classes.timeBlock)}
                          style={{
                            left: `${block.start}%`,
                            width: `${block.width}%`,
                            backgroundColor: getRoomColor(type),
                          }}
                          onMouseEnter={() => setHoveredBlock({ room, duration: block.duration })}
                          onMouseLeave={() => setHoveredBlock(null)}
                        />
                      </StyledTooltip>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Box>
          <div className={classes.timeAxis}>
            <div className={classes.timeLabels}>
              {Array.from({ length: hourCount }, (_, i) => (
                <div key={i} className={classes.timeLabel}>
                  {formatTimeLabel(i, timeRange)}
                </div>
              ))}
            </div>
          </div>
        </Box>
      </Box>
      <Box className={classes.statsContainer}>
        <div className={classes.progressBar}>
          {sortedStats.map(stat => (
            <div
              key={stat.roomName}
              style={{
                width: `${stat.percentage}%`,
                backgroundColor: getRoomColor(stat.type),
              }}
            />
          ))}
        </div>
        <Box className={classes.statsBar}>
          {sortedStats.map(stat => (
            <div
              key={stat.roomName}
              className={classes.statItem}
              style={{
                width: `${stat.percentage}%`,
              }}
            >
              <div className="header">
                <div className="dot" style={{ backgroundColor: getRoomColor(stat.type) }} />
                <span className="label">{stat.roomName}</span>
              </div>
              <div className="details">
                <span className="percentage">{stat.percentage}%</span>
                <span className="time">{formatDuration(stat.duration)}</span>
                <span className="cost">${stat.cost}</span>
              </div>
            </div>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TimelineGraph;
